"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { FEATURE_FLAGS } from "@/lib/feature-flags/flags";
import {
  CUSTOMER_WINDOW_MAX_BYTES,
  CUSTOMER_WINDOW_PROTOCOL,
  CustomerWindowAuthorizationSchema,
  CustomerWindowServerMessageSchema,
} from "@/lib/realtime/customer-projection/window-contract";
import { HubSocketMessageSchema } from "@/lib/realtime/hub-events";

import { useAuth } from "@/dashboard/contexts/auth-context";
import { useFeatureFlags } from "@/dashboard/contexts/feature-context";
import {
  CUSTOMER_DETAIL_QUERY_KEY_PREFIX,
  customerDetailQueryKey,
} from "@/dashboard/features/hub/hooks/customer/use-customer-detail-query";
import { requestCustomerListRefresh } from "@/dashboard/features/hub/hooks/customer/use-customer-list-refresh-budget";
import { CUSTOMERS_QUERY_KEY_PREFIX } from "@/dashboard/features/hub/hooks/customer/use-customers-query";
import { customerFoldersQueryKey } from "@/dashboard/features/hub/lib/folders/use-customer-folders-query";
import { folderCountsQueryKey } from "@/dashboard/features/hub/lib/folders/use-folder-counts-query";
import { HubEventCoalescer } from "@/dashboard/features/hub/lib/realtime/hub-event-coalescer";
import {
  HubRealtimeContext,
  CustomerListWindowTransportContext,
  type CustomerListWindowTransport,
  type HubRealtimeContextValue,
} from "@/dashboard/features/hub/realtime/hub-realtime-status";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { tenantKey } from "@/dashboard/hooks/query/use-tenant-query";
import { HttpError } from "@/dashboard/lib/api/errors";

import type { RealtimeTicketResponse } from "@/app/api/dashboard/realtime/ticket/route";

const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const REAUTH_INTERVAL_MS = 45_000;
const DISCONNECTED: HubRealtimeContextValue = {
  connected: false,
  customerListsConnected: false,
  folderCountsConnected: false,
};

function sameTenantScope(left: ReadonlySet<string>, right: readonly string[]): boolean {
  return left.size === right.length && right.every(tenantId => left.has(tenantId));
}

/** One authenticated socket; each capability controls only its own Hub refresh surfaces. */
export function HubRealtimeProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { activeTenantId, isAccountOverride, isAuthenticated, isSpoofing, tenants, user } =
    useAuth();
  const { hasFeature } = useFeatureFlags();
  const customerListsEnabled = hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_REALTIME);
  const refreshBudgetEnabled = hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_REFRESH_BUDGET);
  const countsEnabled = hasFeature(FEATURE_FLAGS.HUB_FOLDER_COUNTS_REALTIME);
  const windowsEnabled =
    hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_DELTAS) &&
    hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_PROJECTION) &&
    customerListsEnabled &&
    refreshBudgetEnabled;
  const enabled = isAuthenticated && !!activeTenantId;
  // ALL alone does not identify the account, spoofed user or permitted tenants.
  const authScopeKey = useMemo(
    () =>
      [
        user?.id ?? "anonymous",
        activeTenantId ?? "none",
        isAccountOverride ? "override" : "standard",
        isSpoofing ? "spoof" : "direct",
        ...tenants.map(tenant => tenant.id).sort(),
      ].join("|"),
    [activeTenantId, isAccountOverride, isSpoofing, tenants, user?.id]
  );
  const previousScope = useRef<{ key: string; tenantId: string } | null>(null);
  const [realtimeState, setRealtimeState] = useState(DISCONNECTED);
  const [windowTransport, setWindowTransport] = useState<CustomerListWindowTransport | null>(null);

  useEffect(() => {
    const selectedTenantId = activeTenantId ?? "";
    const customerKey = tenantKey(selectedTenantId, CUSTOMERS_QUERY_KEY_PREFIX);
    const countKey = folderCountsQueryKey(selectedTenantId);
    const clearScopeQueries = (tenantId: string) => {
      for (const queryKey of [
        tenantKey(tenantId, CUSTOMERS_QUERY_KEY_PREFIX),
        folderCountsQueryKey(tenantId),
      ]) {
        void queryClient.cancelQueries({ queryKey });
        queryClient.removeQueries({ queryKey });
      }
    };
    if (previousScope.current && previousScope.current.key !== authScopeKey) {
      clearScopeQueries(previousScope.current.tenantId);
      if (previousScope.current.tenantId !== selectedTenantId) {
        clearScopeQueries(selectedTenantId);
      }
    }
    previousScope.current = { key: authScopeKey, tenantId: selectedTenantId };
    setRealtimeState(DISCONNECTED);
    setWindowTransport(null);
    if (!enabled || !activeTenantId) {
      return;
    }

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reauthTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let epoch = 0;
    let hasEventConnection = false;
    let disposed = false;
    let authorizedTenantIds = new Set<string>();
    let activeCapabilities = new Set<RealtimeTicketResponse["capabilities"][number]>();
    const windowListeners = new Map<string, (message: unknown) => void>();

    const enabledCapabilities = {
      agent_events: false,
      hub_events: true,
      hub_customer_lists: customerListsEnabled,
      hub_folder_counts: countsEnabled,
      hub_customer_list_deltas: windowsEnabled,
      cc_agent: false,
      cc_supervisor: false,
    } satisfies Record<RealtimeTicketResponse["capabilities"][number], boolean>;
    const capabilitiesForTicket = (ticket: RealtimeTicketResponse) =>
      new Set(ticket.capabilities.filter(capability => enabledCapabilities[capability]));
    const updateConnectionState = () => {
      setRealtimeState({
        connected: activeCapabilities.has("hub_events"),
        customerListsConnected: activeCapabilities.has("hub_customer_lists"),
        folderCountsConnected: activeCapabilities.has("hub_folder_counts"),
      });
    };
    const updateWindowTransport = (ticket: RealtimeTicketResponse, ws: WebSocket) => {
      windowListeners.clear();
      const authorization = CustomerWindowAuthorizationSchema.safeParse(
        ticket.customerListProjection
      );
      if (
        !windowsEnabled ||
        !activeCapabilities.has("hub_customer_list_deltas") ||
        !authorization.success
      ) {
        setWindowTransport(null);
        return;
      }
      setWindowTransport({
        authorization: authorization.data,
        tenantIds: ticket.tenantIds,
        subscribe: (subscriptionId, tab, onMessage) => {
          if (disposed || socket !== ws || ws.readyState !== WebSocket.OPEN) {
            return () => {};
          }
          windowListeners.set(subscriptionId, onMessage);
          ws.send(
            JSON.stringify({
              type: "customer_window_subscribe",
              protocolVersion: CUSTOMER_WINDOW_PROTOCOL,
              authorizationId: authorization.data.authorizationId,
              subscriptionId,
              tab,
            })
          );
          return () => {
            windowListeners.delete(subscriptionId);
            if (!disposed && socket === ws && ws.readyState === WebSocket.OPEN) {
              ws.send(
                JSON.stringify({
                  type: "customer_window_unsubscribe",
                  protocolVersion: CUSTOMER_WINDOW_PROTOCOL,
                  subscriptionId,
                })
              );
            }
          };
        },
      });
    };
    const listCoalescer = new HubEventCoalescer(batch => {
      if (
        disposed ||
        (!activeCapabilities.has("hub_events") && !activeCapabilities.has("hub_customer_lists"))
      ) {
        return;
      }
      if (batch.customers) {
        void requestCustomerListRefresh(queryClient, customerKey, {
          enabled: refreshBudgetEnabled,
          reason: batch.resync ? "resync" : "event",
        });
      }
    });
    const detailCoalescer = new HubEventCoalescer(batch => {
      if (disposed || !activeCapabilities.has("hub_events")) {
        return;
      }
      if (batch.allCustomerDetails) {
        void queryClient.invalidateQueries({
          queryKey: tenantKey(activeTenantId, CUSTOMER_DETAIL_QUERY_KEY_PREFIX),
        });
        void queryClient.invalidateQueries({
          queryKey: ["customers"],
          predicate: query => query.queryKey[2] === "folders",
        });
      } else {
        for (const endUserId of batch.endUserIds) {
          void queryClient.invalidateQueries({
            queryKey: tenantKey(activeTenantId, ...customerDetailQueryKey(endUserId)),
          });
          void queryClient.invalidateQueries({ queryKey: customerFoldersQueryKey(endUserId) });
        }
      }
    });
    const countCoalescer = new HubEventCoalescer(() => {
      if (
        !disposed &&
        (activeCapabilities.has("hub_events") || activeCapabilities.has("hub_folder_counts"))
      ) {
        void queryClient.invalidateQueries({ queryKey: countKey });
      }
    });
    const clearReauth = () => {
      if (reauthTimer) {
        clearTimeout(reauthTimer);
        reauthTimer = null;
      }
    };
    const clearConnection = () => {
      clearReauth();
      activeCapabilities.clear();
      authorizedTenantIds.clear();
      windowListeners.clear();
      setWindowTransport(null);
      listCoalescer.dispose();
      detailCoalescer.dispose();
      countCoalescer.dispose();
      setRealtimeState(DISCONNECTED);
    };
    const scheduleReconnect = () => {
      if (disposed) {
        return;
      }
      const delay =
        Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS) *
        (0.5 + Math.random() / 2);
      attempt++;
      reconnectTimer = setTimeout(() => void connect(), delay);
    };
    const refreshTicket = () =>
      fetchJson<RealtimeTicketResponse>("/api/dashboard/realtime/ticket", {
        method: "POST",
        tenantId: activeTenantId,
        ...(windowsEnabled ? { body: JSON.stringify({ customerListProjection: true }) } : {}),
      });

    const scheduleReauth = (ws: WebSocket) => {
      clearReauth();
      reauthTimer = setTimeout(async () => {
        try {
          const ticket = await refreshTicket();
          if (disposed || socket !== ws || ws.readyState !== WebSocket.OPEN) {
            return;
          }
          if (!sameTenantScope(authorizedTenantIds, ticket.tenantIds)) {
            // Cancel old ALL requests before any response can restore a revoked tenant.
            clearScopeQueries(activeTenantId);
            clearConnection();
            ws.close();
            return;
          }
          const hadEvents = activeCapabilities.has("hub_events");
          const hadCustomerLists = activeCapabilities.has("hub_customer_lists");
          const hadCounts = activeCapabilities.has("hub_folder_counts");
          const nextCapabilities = capabilitiesForTicket(ticket);
          const changed =
            nextCapabilities.size !== activeCapabilities.size ||
            [...nextCapabilities].some(capability => !activeCapabilities.has(capability));
          if (changed) {
            epoch++;
          }
          ws.send(JSON.stringify({ type: "reauth", epoch, ticket: ticket.ticket }));
          activeCapabilities = nextCapabilities;
          updateConnectionState();
          updateWindowTransport(ticket, ws);
          if (!hadEvents && activeCapabilities.has("hub_events")) {
            listCoalescer.push({ type: "resync" });
            detailCoalescer.push({ type: "resync" });
            countCoalescer.push({ type: "resync" });
          }
          if (!hadCustomerLists && activeCapabilities.has("hub_customer_lists")) {
            listCoalescer.push({ type: "resync" });
          }
          if (!hadCounts && activeCapabilities.has("hub_folder_counts")) {
            countCoalescer.push({ type: "resync" });
          }
          scheduleReauth(ws);
        } catch {
          if (!disposed && socket === ws) {
            clearConnection();
            ws.close();
          }
        }
      }, REAUTH_INTERVAL_MS);
    };

    const connect = async () => {
      let ticket: RealtimeTicketResponse;
      try {
        ticket = await refreshTicket();
      } catch (error) {
        if (!(error instanceof HttpError && error.status === 404)) {
          scheduleReconnect();
        }
        return;
      }
      if (disposed) {
        return;
      }
      const url = new URL(ticket.url);
      url.searchParams.set("ticket", ticket.ticket);
      const ws = new WebSocket(url);
      socket = ws;

      ws.onopen = () => {
        if (disposed || socket !== ws) {
          ws.close();
          return;
        }
        attempt = 0;
        epoch++;
        authorizedTenantIds = new Set(ticket.tenantIds);
        activeCapabilities = capabilitiesForTicket(ticket);
        updateConnectionState();
        updateWindowTransport(ticket, ws);
        const hasEvents = activeCapabilities.has("hub_events");
        const hasCustomerLists = activeCapabilities.has("hub_customer_lists");
        if (hasEvents && hasEventConnection) {
          listCoalescer.push({ type: "resync" });
          detailCoalescer.push({ type: "resync" });
          countCoalescer.push({ type: "resync" });
        }
        hasEventConnection ||= hasEvents;
        // A list-capability socket must resync on first authorization as well as reconnect;
        // an initial HTTP read can land just before the socket gets authorized.
        if (hasCustomerLists) {
          listCoalescer.push({ type: "resync" });
        }
        // Includes the first connection: a write can land between the initial HTTP read
        // and socket authorization. The same delay also gives the replica time to catch up.
        if (activeCapabilities.has("hub_folder_counts")) {
          countCoalescer.push({ type: "resync" });
        }
        scheduleReauth(ws);
      };
      ws.onmessage = event => {
        if (disposed || socket !== ws) {
          return;
        }
        let json: unknown;
        try {
          if (
            windowsEnabled &&
            new TextEncoder().encode(String(event.data)).length > CUSTOMER_WINDOW_MAX_BYTES
          ) {
            for (const listener of [...windowListeners.values()]) {
              listener(null);
            }
            return;
          }
          json = JSON.parse(String(event.data));
        } catch {
          if (windowsEnabled) {
            for (const listener of [...windowListeners.values()]) {
              listener(null);
            }
          }
          return;
        }
        if (
          windowsEnabled &&
          typeof json === "object" &&
          json &&
          "type" in json &&
          typeof json.type === "string" &&
          json.type.startsWith("customer_window_")
        ) {
          const parsedWindow = CustomerWindowServerMessageSchema.safeParse(json);
          if (parsedWindow.success) {
            windowListeners.get(parsedWindow.data.subscriptionId)?.(parsedWindow.data);
          } else {
            for (const listener of [...windowListeners.values()]) {
              listener(null);
            }
          }
          return;
        }
        const parsed = HubSocketMessageSchema.safeParse(json);
        if (!parsed.success) {
          return;
        }
        const message = parsed.data;
        if (message.type === "event" && !authorizedTenantIds.has(message.tenantId)) {
          return;
        }
        if (activeCapabilities.has("hub_events") || activeCapabilities.has("hub_customer_lists")) {
          listCoalescer.push(message);
        }
        if (activeCapabilities.has("hub_events")) {
          detailCoalescer.push(message);
        }
        if (
          activeCapabilities.has("hub_events") ||
          (activeCapabilities.has("hub_folder_counts") &&
            (message.type === "resync" || message.kind !== "assignment"))
        ) {
          countCoalescer.push(message);
        }
      };
      ws.onclose = () => {
        if (disposed || socket !== ws) {
          return;
        }
        socket = null;
        clearConnection();
        scheduleReconnect();
      };
    };

    void connect();
    return () => {
      disposed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      clearReauth();
      listCoalescer.dispose();
      detailCoalescer.dispose();
      countCoalescer.dispose();
      socket?.close();
    };
  }, [
    activeTenantId,
    authScopeKey,
    countsEnabled,
    customerListsEnabled,
    enabled,
    queryClient,
    refreshBudgetEnabled,
    windowsEnabled,
  ]);

  return (
    <HubRealtimeContext.Provider value={realtimeState}>
      <CustomerListWindowTransportContext.Provider value={windowTransport}>
        {children}
      </CustomerListWindowTransportContext.Provider>
    </HubRealtimeContext.Provider>
  );
}
